import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import api from "../../lib/api";
import { Users, CheckCircle2, AlertCircle, Bus, ScanLine, ShieldCheck, ShieldX } from "lucide-react";

function StatCard({ label, value, sub, icon: Icon, accent, testid }) {
    return (
        <div
            data-testid={testid}
            className="bg-white border border-[#E5E5E5] p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
        >
            <div className="flex items-start justify-between mb-6">
                <div className={`w-10 h-10 flex items-center justify-center ${accent}`}>
                    <Icon size={20} />
                </div>
                <span className="label-caps">{label}</span>
            </div>
            <div className="font-display text-5xl text-[#111111] leading-none mb-2">{value}</div>
            {sub && <p className="text-xs text-[#666666]">{sub}</p>}
        </div>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);

    const load = async () => {
        const [s, r] = await Promise.all([
            api.get("/dashboard/stats"),
            api.get("/attendance", { params: { limit: 8 } }),
        ]);
        setStats(s.data);
        setRecent(r.data);
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, []);

    return (
        <AdminLayout>
            <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <p className="label-caps mb-2">Overview</p>
                        <h1 className="font-display text-5xl text-[#111111]">Dashboard</h1>
                    </div>
                    <div className="text-right">
                        <p className="label-caps">Today</p>
                        <p className="font-mono text-sm text-[#111111]">
                            {new Date().toLocaleDateString(undefined, {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            })}
                        </p>
                    </div>
                </div>

                {stats && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                            <StatCard
                                label="Total Students"
                                value={stats.total_students}
                                sub="Active in system"
                                icon={Users}
                                accent="bg-[#111111] text-white"
                                testid="stat-total-students"
                            />
                            <StatCard
                                label="Paid"
                                value={stats.paid_students}
                                sub="Payment verified"
                                icon={CheckCircle2}
                                accent="bg-[#34C759] text-white"
                                testid="stat-paid-students"
                            />
                            <StatCard
                                label="Payment Due"
                                value={stats.unpaid_students}
                                sub="Action required"
                                icon={AlertCircle}
                                accent="bg-[#FF3B30] text-white"
                                testid="stat-unpaid-students"
                            />
                            <StatCard
                                label="Buses"
                                value={stats.total_buses}
                                sub="Active routes"
                                icon={Bus}
                                accent="bg-[#111111] text-white"
                                testid="stat-buses"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                            <StatCard
                                label="Today Scans"
                                value={stats.today_scans}
                                sub="Total boarding attempts"
                                icon={ScanLine}
                                accent="bg-[#111111] text-white"
                                testid="stat-today-scans"
                            />
                            <StatCard
                                label="Allowed Today"
                                value={stats.today_allowed}
                                sub="Permitted to board"
                                icon={ShieldCheck}
                                accent="bg-[#34C759] text-white"
                                testid="stat-today-allowed"
                            />
                            <StatCard
                                label="Blocked Today"
                                value={stats.today_blocked}
                                sub="Payment due / invalid"
                                icon={ShieldX}
                                accent="bg-[#FF3B30] text-white"
                                testid="stat-today-blocked"
                            />
                        </div>
                    </>
                )}

                <div className="bg-white border border-[#E5E5E5]">
                    <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#111111]">
                        <h2 className="font-display text-xl text-[#111111]">Recent Scans</h2>
                        <span className="label-caps">Live · auto-refresh</span>
                    </div>
                    {recent.length === 0 ? (
                        <div className="p-10 text-center text-[#666666]">
                            No scans yet. Use the Operator PWA to begin.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F8F9FA] border-b-2 border-[#111111]">
                                <tr>
                                    <th className="text-left font-bold py-3 px-4">Student</th>
                                    <th className="text-left font-bold py-3 px-4">Bus / Route</th>
                                    <th className="text-left font-bold py-3 px-4">Operator</th>
                                    <th className="text-left font-bold py-3 px-4">Time</th>
                                    <th className="text-left font-bold py-3 px-4">Result</th>
                                </tr>
                            </thead>
                            <tbody data-testid="recent-scans-table">
                                {recent.map((r) => (
                                    <tr
                                        key={r.id}
                                        className="border-b border-[#E5E5E5] hover:bg-[#F8F9FA] transition-colors"
                                    >
                                        <td className="py-3 px-4">
                                            <div className="font-medium">{r.student_name}</div>
                                            <div className="text-xs text-[#666666] font-mono">
                                                {r.student_roll || "—"}
                                            </div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="font-medium">{r.bus_name}</div>
                                            <div className="text-xs text-[#666666]">{r.route_name}</div>
                                        </td>
                                        <td className="py-3 px-4 text-[#666666]">{r.operator_name}</td>
                                        <td className="py-3 px-4 font-mono text-xs text-[#666666]">
                                            {new Date(r.scan_time).toLocaleString()}
                                        </td>
                                        <td className="py-3 px-4">
                                            <ResultBadge result={r.result} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}

export function ResultBadge({ result }) {
    const map = {
        allowed: { cls: "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30", label: "Allowed" },
        blocked: { cls: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30", label: "Blocked" },
        invalid: { cls: "bg-[#666666]/10 text-[#666666] border-[#666666]/30", label: "Invalid" },
    };
    const m = map[result] || map.invalid;
    return (
        <span
            className={`inline-block ${m.cls} font-bold px-2 py-1 text-xs uppercase tracking-wider border`}
        >
            {m.label}
        </span>
    );
}
